// Cold-room slots, cases and release (docs/plans/phase2-mortuary-and-notifications.md §2.5, §5).

import { err, ok, type Result } from '../result.ts';

export const MORTUARY_CASE_SOURCES = ['facility_death', 'brought_in'] as const;
export type MortuaryCaseSource = (typeof MORTUARY_CASE_SOURCES)[number];

export const MORTUARY_CASE_STATUSES = ['in_storage', 'released', 'entered_in_error'] as const;
export type MortuaryCaseStatus = (typeof MORTUARY_CASE_STATUSES)[number];

export const MORTUARY_SLOT_STATUSES = ['active', 'out_of_service', 'retired'] as const;
export type MortuarySlotStatus = (typeof MORTUARY_SLOT_STATUSES)[number];

/** TODO(legal-verify) M2: the identity documents a collector may show; provisional. */
export const COLLECTOR_ID_TYPES = [
  'national_id',
  'passport',
  'alien_id',
  'birth_certificate',
  'other',
] as const;
export type CollectorIdType = (typeof COLLECTOR_ID_TYPES)[number];

export type SlotBoardState = 'occupied' | 'free' | 'out_of_service' | 'retired';

/**
 * What the board shows for a slot. Occupancy is not a column: a slot is occupied when an
 * open assignment points at it (as beds), and that wins over its status.
 */
export function slotBoardState(status: MortuarySlotStatus, occupied: boolean): SlotBoardState {
  if (occupied) return 'occupied';
  return status === 'active' ? 'free' : status;
}

export type SlotStatusErrorCode =
  'slot_status.occupied' | 'slot_status.reason_required' | 'slot_status.retired';

/**
 * A free slot can be taken out of service or retired with a reason, and put back in
 * service; a slot with a body in it can't change, and a retired slot stays retired.
 */
export function slotStatusChange(
  from: MortuarySlotStatus,
  to: MortuarySlotStatus,
  facts: { occupied: boolean; reason: string | null },
): Result<MortuarySlotStatus, SlotStatusErrorCode> {
  if (from === 'retired') return err('slot_status.retired');
  if (facts.occupied) return err('slot_status.occupied');
  if (to !== 'active' && (facts.reason ?? '').trim() === '') {
    return err('slot_status.reason_required');
  }
  return ok(to);
}

export type ReleaseReadinessErrorCode = 'release.request_open' | 'release.not_in_storage';

/** A body can be asked for once it is in storage and no other request is waiting. */
export function releaseReadiness(facts: {
  status: MortuaryCaseStatus;
  openRequest: boolean;
}): Result<'can_request', ReleaseReadinessErrorCode> {
  if (facts.status !== 'in_storage') return err('release.not_in_storage');
  if (facts.openRequest) return err('release.request_open');
  return ok('can_request');
}
